import { useEffect, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import EditablePage from "@/components/pagebuilder/EditablePage";
import api from "@/lib/api";
import { MapPin, Phone } from "lucide-react";
import { useT } from "@/context/LanguageContext";
import { localizedContent } from "@/lib/localize";

const hideOnError = (e) => {
  e.currentTarget.style.display = "none";
};

export default function About() {
  const { lang } = useT();
  const [page, setPage] = useState(null);
  useEffect(() => {
    api.get("/cms/pages/about").then((r) => setPage(r.data)).catch(() => {});
  }, []);
  const c = localizedContent(page?.content, lang);
  const values = c.values || [];
  const instructors = c.instructors || [];

  return (
    <PublicLayout>
      <EditablePage slug="about" page={page} onSaved={setPage}>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28" data-testid="about-page">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dojo-ink-soft)] mb-4">{c.eyebrow || "About Us"}</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] mb-12 max-w-4xl">
          {c.heading || "A network of dojos serving Chicagoland"}
        </h1>
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6">
            {c.image && (
              <img
                src={c.image}
                alt="Yoshitaka Karate"
                referrerPolicy="no-referrer"
                loading="lazy"
                onError={hideOnError}
                className="w-full aspect-[4/3] object-cover shadow-lg"
              />
            )}
          </div>
          <div className="md:col-span-6">
            <p className="text-lg md:text-xl leading-relaxed text-[var(--dojo-ink)] mb-8 font-light">
              {c.body}
            </p>
            {c.quote && (
              <>
                <div className="brush-divider my-8" />
                <blockquote className="font-serif text-2xl md:text-3xl italic leading-snug text-[var(--dojo-ink)] max-w-2xl">
                  “{c.quote}”
                </blockquote>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Values */}
      {values.length > 0 && (
        <section className="bg-[var(--dojo-paper-alt)] border-y border-[var(--dojo-border)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24">
            <div className="grid md:grid-cols-4 gap-0 border border-[var(--dojo-border)] bg-[var(--dojo-paper)]">
              {values.map((v, i) => (
                <div
                  key={i}
                  className={`p-8 ${i < values.length - 1 ? "md:border-r border-[var(--dojo-border)]" : ""} ${i > 0 ? "border-t md:border-t-0 border-[var(--dojo-border)]" : ""}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dojo-ink-soft)] mb-2">0{i + 1}</div>
                  <h3 className="font-serif text-2xl mb-3">{v.title}</h3>
                  <p className="text-sm text-[var(--dojo-ink-soft)] leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instructors */}
      {instructors.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-10">{c.instructors_heading || "Instructors"}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {instructors.map((ins, i) => (
              <div key={i} className="bg-[var(--dojo-paper-alt)] border border-[var(--dojo-border)] p-8 flex flex-col items-center text-center">
                {ins.image && (
                  <img
                    src={ins.image}
                    alt={ins.name}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    onError={hideOnError}
                    className="w-24 h-24 rounded-full object-cover border-2 border-[var(--dojo-green)] mb-5"
                  />
                )}
                <h3 className="font-serif text-xl mb-3">{ins.name}</h3>
                {ins.location && (
                  <div className="flex items-center gap-2 text-sm text-[var(--dojo-ink-soft)] mb-1">
                    <MapPin size={14} className="text-[var(--dojo-green)]" /> {ins.location}
                  </div>
                )}
                {ins.phone && (
                  <a href={`tel:${ins.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 text-sm text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-green)]">
                    <Phone size={14} className="text-[var(--dojo-green)]" /> {ins.phone}
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      </EditablePage>
    </PublicLayout>
  );
}
